<!-- The (click) on the <dialog> is event-delegation for the backdrop
     close + the inner anchor close. Escape is handled explicitly via
     (keydown.escape)/(cancel) so an open section collapses before the
     drawer itself dismisses. tabindex="-1" makes the dialog a valid
     (non-Tab-order) focus target for interactive-supports-focus — it
     already has a (keydown.escape) handler, so this is a real fix rather
     than a suppression. -->
<dialog
  #drawerDialog
  class="drawer"
  aria-label="Site navigation"
  tabindex="-1"
  (click)="handleDialogClick($event)"
  (keydown.escape)="onEscape($event)"
  (cancel)="onCancel($event)"
  (close)="drawerClosed.emit()"
>
  <button
    type="button"
    class="drawer-close groove-ring"
    aria-label="Close navigation"
    (click)="close()"
  >
    ×
  </button>

  <nav class="drawer-nav" aria-label="Main">
    @for (group of navGroups; track group.label) {
      @switch (group.kind) {
        @case ("link") {
          <!-- Childless group (Blog) — a plain top-level link styled to
               read as a peer of the disclosure triggers, kept in natural
               Tab order alongside them. -->
          <a
            grooveListLink
            class="drawer-trigger-link"
            [routerLink]="group.href"
            routerLinkActive="drawer-link--active"
            [routerLinkActiveOptions]="{ exact: group.href === '/' }"
            ariaCurrentWhenActive="page"
          >
            {{ group.label }}
          </a>
        }
        @default {
          <div class="drawer-acc-item">
            <h2 class="drawer-acc-heading">
              <button
                grooveListLink
                [disclosure]="true"
                type="button"
                [attr.aria-expanded]="openSection() === group.label"
                [attr.aria-controls]="sectionPanelId(group.label)"
                (click)="toggleSection(group.label)"
              >
                {{ group.label }}
              </button>
            </h2>
            <div
              class="drawer-acc-panel"
              [id]="sectionPanelId(group.label)"
              [hidden]="openSection() !== group.label"
            >
              @if (group.kind === "panel") {
                @for (section of group.sections; track section.label) {
                  <p class="groove-type-eyebrow">{{ section.label }}</p>
                  @for (link of section.links; track link.href) {
                    <a
                      grooveListLink
                      [indent]="true"
                      [routerLink]="link.href"
                      routerLinkActive="drawer-link--active"
                      ariaCurrentWhenActive="page"
                    >
                      {{ link.label }}
                    </a>
                  }
                }
              } @else {
                @for (link of group.links; track link.href) {
                  <a
                    grooveListLink
                    [indent]="true"
                    [routerLink]="link.href"
                    routerLinkActive="drawer-link--active"
                    ariaCurrentWhenActive="page"
                  >
                    {{ link.label }}
                  </a>
                }
              }
            </div>
          </div>
        }
      }
    }
  </nav>

  <!-- Contact — phone + email so they're reachable from the nav without
       scrolling to the footer. Data comes from DATA_CONFIG (same derivation as
       the footer) so number/address stay in sync site-wide. -->
  <address class="drawer-contact">
    <a
      class="drawer-contact-link groove-ring"
      [href]="phoneHref"
      (click)="close()"
      >{{ phoneDisplay }}</a
    >
    <a
      class="drawer-contact-link groove-ring"
      [href]="emailHref"
      (click)="close()"
      >{{ emailDisplay }}</a
    >
  </address>

  <!-- Social links — pulled out of the chrome shelf on mobile; surfaced here
       (and in the footer) so they stay one tap away from the primary nav. -->
  <ul class="drawer-socials" role="list" aria-label="Social media">
    @for (s of socialLinks; track s.key) {
      <li>
        <a
          class="drawer-social groove-ring"
          [href]="s.href"
          [attr.aria-label]="s.label"
          target="_blank"
          rel="noopener noreferrer"
          (click)="close()"
        >
          <span
            class="drawer-social-icon"
            [style.mask-image]="s.iconUrl"
            aria-hidden="true"
          ></span>
        </a>
      </li>
    }
  </ul>
</dialog>
